// astriaVietnamCopy.ts
// ─── Astria Vietnam — English/Vietnamese UI copy ───────────────────────────
// All fixed UI strings for the shell header, shared wizard/card chrome, and
// each of the 5 lanes' questions/helpers/option labels/result-field labels/
// buttons. The backend reading text itself (message.content /
// astriaVietnamData field values) is NOT translated here — that comes back
// from the model already in the selected language (see
// backend/helper/vietnam/astriaVietnamPromptService.js's
// resolveVietnamTarget). This file only localizes the surrounding UI
// chrome. Same pattern as utils/India/astriaIndiaV2Copy.ts.
import { AstriaVietnamLanguage } from "./astriaVietnamLanguage";

export interface OptionCopy {
  value: string;
  label: string;
}

interface ShellCopy {
  title: string;
  subtitle: string;
  langToggleLabel: string;
}

interface SharedCopy {
  stepOf: (step: number, total: number) => string;
  back: string;
  continueLabel: string;
  tryAgain: string;
  continueInChat: string;
  startNewReading: string;
  genderLabel: string;
  genderOptions: OptionCopy[];
  dobHourLabel: string;
  dobHourPlaceholder: string;
  dobHourOptions: OptionCopy[];
  dayPlaceholder: string;
  monthPlaceholder: string;
  yearPlaceholder: string;
}

interface BirthChartCopy {
  tabLabel: string;
  tabSublabel: string;
  loading: string;
  resultTitle: string;
  step1Question: string;
  step1Helper: string;
  revealButton: string;
  fields: {
    summary: string;
    cungMenhMeaning: string;
    cungThanMeaning: string;
    hoaKhiEffect: string;
    currentCycle: string;
    guidance: string;
  };
}

interface LuckyDayCopy {
  tabLabel: string;
  tabSublabel: string;
  loading: string;
  resultTitle: string;
  step1Question: string;
  step1Helper: string;
  purposeLabel: string;
  purposePlaceholder: string;
  purposeOptions: OptionCopy[];
  revealButton: string;
  fields: {
    lunarDate: string;
    suitability: string;
    bestFor: string;
    cautionNote: string;
    guidance: string;
  };
}

interface CompatibilityCopy {
  tabLabel: string;
  tabSublabel: string;
  loading: string;
  resultTitle: string;
  step1Question: string;
  step1Helper: string;
  step2Question: string;
  step2Helper: string;
  partnerDobHourPlaceholder: string;
  revealButton: string;
  scoreLabel: (score: number) => string;
  fields: {
    rhythmBetween: string;
    harmonyLevel: string;
    frictionPoint: string;
    connectionPath: string;
  };
}

interface SpaceEnergyCopy {
  tabLabel: string;
  tabSublabel: string;
  loading: string;
  resultTitle: string;
  step1Question: string;
  spaceTypeLabel: string;
  spaceTypePlaceholder: string;
  spaceTypeOptions: OptionCopy[];
  directionLabel: string;
  directionPlaceholder: string;
  directionOptions: OptionCopy[];
  revealButton: string;
  fields: {
    spaceReading: string;
    directionNote: string;
    energySuggestion: string;
    guidance: string;
  };
}

interface TarotCopy {
  tabLabel: string;
  tabSublabel: string;
  loading: string;
  resultTitle: string;
  step1Question: string;
  topicLabel: string;
  topicPlaceholder: string;
  topicOptions: OptionCopy[];
  revealButton: string;
  fields: {
    cardMeaning: string;
    reflection: string;
    guidance: string;
  };
}

interface AstriaVietnamCopyShape {
  shell: ShellCopy;
  shared: SharedCopy;
  birthChart: BirthChartCopy;
  luckyDay: LuckyDayCopy;
  compatibility: CompatibilityCopy;
  spaceEnergy: SpaceEnergyCopy;
  tarot: TarotCopy;
}

const CHI_OPTIONS_VI: OptionCopy[] = [
  { value: "Tý", label: "Tý (23h-1h)" },
  { value: "Sửu", label: "Sửu (1h-3h)" },
  { value: "Dần", label: "Dần (3h-5h)" },
  { value: "Mão", label: "Mão (5h-7h)" },
  { value: "Thìn", label: "Thìn (7h-9h)" },
  { value: "Tỵ", label: "Tỵ (9h-11h)" },
  { value: "Ngọ", label: "Ngọ (11h-13h)" },
  { value: "Mùi", label: "Mùi (13h-15h)" },
  { value: "Thân", label: "Thân (15h-17h)" },
  { value: "Dậu", label: "Dậu (17h-19h)" },
  { value: "Tuất", label: "Tuất (19h-21h)" },
  { value: "Hợi", label: "Hợi (21h-23h)" },
];

const CHI_OPTIONS_EN: OptionCopy[] = [
  { value: "Tý", label: "Tý (11pm-1am)" },
  { value: "Sửu", label: "Sửu (1am-3am)" },
  { value: "Dần", label: "Dần (3am-5am)" },
  { value: "Mão", label: "Mão (5am-7am)" },
  { value: "Thìn", label: "Thìn (7am-9am)" },
  { value: "Tỵ", label: "Tỵ (9am-11am)" },
  { value: "Ngọ", label: "Ngọ (11am-1pm)" },
  { value: "Mùi", label: "Mùi (1pm-3pm)" },
  { value: "Thân", label: "Thân (3pm-5pm)" },
  { value: "Dậu", label: "Dậu (5pm-7pm)" },
  { value: "Tuất", label: "Tuất (7pm-9pm)" },
  { value: "Hợi", label: "Hợi (9pm-11pm)" },
];

export const ASTRIA_VIETNAM_COPY: Record<
  AstriaVietnamLanguage,
  AstriaVietnamCopyShape
> = {
  vi: {
    shell: {
      title: "Astria Việt Nam",
      subtitle: "Tử Vi, xem ngày, hòa hợp, phong thủy và Tarot",
      langToggleLabel: "Ngôn ngữ",
    },
    shared: {
      stepOf: (step, total) => `Bước ${step}/${total}`,
      back: "Quay lại",
      continueLabel: "Tiếp tục",
      tryAgain: "Thử lại",
      continueInChat: "Tiếp tục trò chuyện",
      startNewReading: "Xem lại từ đầu",
      genderLabel: "Giới tính",
      genderOptions: [
        { value: "male", label: "Nam" },
        { value: "female", label: "Nữ" },
      ],
      dobHourLabel: "Giờ sinh",
      dobHourPlaceholder: "Chọn giờ sinh",
      dobHourOptions: CHI_OPTIONS_VI,
      dayPlaceholder: "Ngày",
      monthPlaceholder: "Tháng",
      yearPlaceholder: "Năm",
    },
    birthChart: {
      tabLabel: "Lá Số Tử Vi",
      tabSublabel: "Birth Chart",
      loading: "Đang xem lá số của bạn...",
      resultTitle: "Lá Số Tử Vi Của Bạn",
      step1Question: "Nhập ngày sinh để xem lá số của bạn",
      step1Helper: "Ngày sinh dương lịch và giờ sinh sẽ được dùng để tính lá số.",
      revealButton: "Xem lá số",
      fields: {
        summary: "Tổng quan",
        cungMenhMeaning: "Cung Mệnh",
        cungThanMeaning: "Cung Thân",
        hoaKhiEffect: "Năng lượng Hóa Khí",
        currentCycle: "Chu kỳ hiện tại",
        guidance: "Hướng đi nhẹ nhàng",
      },
    },
    luckyDay: {
      tabLabel: "Xem Ngày Tốt",
      tabSublabel: "Lucky Day",
      loading: "Đang xem ngày...",
      resultTitle: "Ngày Bạn Đã Chọn",
      step1Question: "Bạn muốn xem ngày nào?",
      step1Helper: "Chọn ngày dương lịch cho việc bạn đang nghĩ tới.",
      purposeLabel: "Loại việc",
      purposePlaceholder: "Chọn loại việc",
      purposeOptions: [
        { value: "wedding", label: "Cưới hỏi" },
        { value: "career", label: "Công việc" },
        { value: "moving", label: "Chuyển nhà" },
        { value: "opening", label: "Khai trương" },
        { value: "other", label: "Khác" },
      ],
      revealButton: "Xem ngày hợp",
      fields: {
        lunarDate: "Ngày âm lịch",
        suitability: "Mức độ phù hợp",
        bestFor: "Phù hợp cho",
        cautionNote: "Lưu ý",
        guidance: "Gợi ý",
      },
    },
    compatibility: {
      tabLabel: "Hòa Hợp",
      tabSublabel: "Compatibility",
      loading: "Đang xem sự hòa hợp...",
      resultTitle: "Năng Lượng Giữa Hai Bạn",
      step1Question: "Nhập ngày sinh của bạn",
      step1Helper: "Thông tin này giúp tính lá số của bạn.",
      step2Question: "Nhập ngày sinh của người kia",
      step2Helper: "Nếu không biết giờ sinh của họ, bạn có thể bỏ qua.",
      partnerDobHourPlaceholder: "Giờ sinh của họ (nếu biết)",
      revealButton: "Xem sự hòa hợp",
      scoreLabel: (score) => `${score}/100`,
      fields: {
        rhythmBetween: "Nhịp giữa hai người",
        harmonyLevel: "Mức độ hòa hợp",
        frictionPoint: "Điểm cần lưu ý",
        connectionPath: "Hướng kết nối",
      },
    },
    spaceEnergy: {
      tabLabel: "Phong Thủy Không Gian",
      tabSublabel: "Space Energy",
      loading: "Đang xem không gian...",
      resultTitle: "Không Gian Của Bạn",
      step1Question: "Bạn muốn xem không gian nào?",
      spaceTypeLabel: "Không gian",
      spaceTypePlaceholder: "Chọn không gian",
      spaceTypeOptions: [
        { value: "home", label: "Nhà ở" },
        { value: "bedroom", label: "Phòng ngủ" },
        { value: "desk", label: "Bàn làm việc" },
        { value: "living_room", label: "Phòng khách" },
      ],
      directionLabel: "Hướng nhà (nếu có)",
      directionPlaceholder: "Chọn hướng (không bắt buộc)",
      directionOptions: [
        { value: "Đông", label: "Đông" },
        { value: "Tây", label: "Tây" },
        { value: "Nam", label: "Nam" },
        { value: "Bắc", label: "Bắc" },
        { value: "Đông Nam", label: "Đông Nam" },
        { value: "Đông Bắc", label: "Đông Bắc" },
        { value: "Tây Nam", label: "Tây Nam" },
        { value: "Tây Bắc", label: "Tây Bắc" },
      ],
      revealButton: "Xem gợi ý năng lượng",
      fields: {
        spaceReading: "Cảm nhận không gian",
        directionNote: "Về hướng",
        energySuggestion: "Gợi ý điều chỉnh",
        guidance: "Hướng đi nhẹ nhàng",
      },
    },
    tarot: {
      tabLabel: "Rút Bài Tarot",
      tabSublabel: "Tarot Reading",
      loading: "Đang rút bài...",
      resultTitle: "Lá Bài Của Bạn",
      step1Question: "Bạn muốn lắng nghe về chủ đề gì?",
      topicLabel: "Chủ đề",
      topicPlaceholder: "Chọn chủ đề",
      topicOptions: [
        { value: "love", label: "Tình cảm" },
        { value: "work", label: "Công việc" },
        { value: "decision", label: "Quyết định" },
        { value: "self", label: "Bản thân" },
      ],
      revealButton: "Rút lá bài",
      fields: {
        cardMeaning: "Ý nghĩa lá bài",
        reflection: "Phản ánh cảm xúc",
        guidance: "Hướng đi nhẹ nhàng",
      },
    },
  },
  en: {
    shell: {
      title: "Astria Vietnam",
      subtitle: "Tử Vi, lucky days, compatibility, feng shui, and tarot",
      langToggleLabel: "Language",
    },
    shared: {
      stepOf: (step, total) => `Step ${step} of ${total}`,
      back: "Back",
      continueLabel: "Continue",
      tryAgain: "Try again",
      continueInChat: "Continue in chat",
      startNewReading: "Start a new reading",
      genderLabel: "Gender",
      genderOptions: [
        { value: "male", label: "Male" },
        { value: "female", label: "Female" },
      ],
      dobHourLabel: "Birth hour",
      dobHourPlaceholder: "Select your birth hour",
      dobHourOptions: CHI_OPTIONS_EN,
      dayPlaceholder: "Day",
      monthPlaceholder: "Month",
      yearPlaceholder: "Year",
    },
    birthChart: {
      tabLabel: "Birth Chart",
      tabSublabel: "Lá Số Tử Vi",
      loading: "Reading your birth chart...",
      resultTitle: "Your Birth Chart",
      step1Question: "Enter your birth date to see your chart",
      step1Helper: "Your solar birth date and hour will be used to compute your chart.",
      revealButton: "View my chart",
      fields: {
        summary: "Summary",
        cungMenhMeaning: "Life Palace (Cung Mệnh)",
        cungThanMeaning: "Body Palace (Cung Thân)",
        hoaKhiEffect: "Hóa Khí Energy",
        currentCycle: "Current Cycle",
        guidance: "Gentle Guidance",
      },
    },
    luckyDay: {
      tabLabel: "Lucky Day",
      tabSublabel: "Xem Ngày Tốt",
      loading: "Checking the day...",
      resultTitle: "Your Chosen Day",
      step1Question: "Which day would you like to check?",
      step1Helper: "Pick the solar calendar date for what you have in mind.",
      purposeLabel: "Purpose",
      purposePlaceholder: "Select a purpose",
      purposeOptions: [
        { value: "wedding", label: "Wedding" },
        { value: "career", label: "Career" },
        { value: "moving", label: "Moving" },
        { value: "opening", label: "Opening a business" },
        { value: "other", label: "Other" },
      ],
      revealButton: "Check this day",
      fields: {
        lunarDate: "Lunar date",
        suitability: "Suitability",
        bestFor: "Best for",
        cautionNote: "Caution",
        guidance: "Guidance",
      },
    },
    compatibility: {
      tabLabel: "Compatibility",
      tabSublabel: "Hòa Hợp",
      loading: "Reading your compatibility...",
      resultTitle: "The Energy Between You",
      step1Question: "Enter your birth date",
      step1Helper: "This is used to compute your own chart.",
      step2Question: "Enter the other person's birth date",
      step2Helper: "If you don't know their birth hour, you can skip it.",
      partnerDobHourPlaceholder: "Their birth hour (if known)",
      revealButton: "View our compatibility",
      scoreLabel: (score) => `${score}/100`,
      fields: {
        rhythmBetween: "Rhythm between you",
        harmonyLevel: "Harmony level",
        frictionPoint: "Something to notice",
        connectionPath: "Path forward",
      },
    },
    spaceEnergy: {
      tabLabel: "Space Energy",
      tabSublabel: "Phong Thủy Không Gian",
      loading: "Reading your space...",
      resultTitle: "Your Space",
      step1Question: "Which space would you like to look at?",
      spaceTypeLabel: "Space type",
      spaceTypePlaceholder: "Select a space",
      spaceTypeOptions: [
        { value: "home", label: "Home" },
        { value: "bedroom", label: "Bedroom" },
        { value: "desk", label: "Desk" },
        { value: "living_room", label: "Living room" },
      ],
      directionLabel: "Facing direction (optional)",
      directionPlaceholder: "Select a direction (optional)",
      directionOptions: [
        { value: "Đông", label: "East" },
        { value: "Tây", label: "West" },
        { value: "Nam", label: "South" },
        { value: "Bắc", label: "North" },
        { value: "Đông Nam", label: "Southeast" },
        { value: "Đông Bắc", label: "Northeast" },
        { value: "Tây Nam", label: "Southwest" },
        { value: "Tây Bắc", label: "Northwest" },
      ],
      revealButton: "View energy suggestions",
      fields: {
        spaceReading: "Space reading",
        directionNote: "About the direction",
        energySuggestion: "Suggested adjustment",
        guidance: "Gentle guidance",
      },
    },
    tarot: {
      tabLabel: "Tarot Reading",
      tabSublabel: "Rút Bài Tarot",
      loading: "Drawing your card...",
      resultTitle: "Your Card",
      step1Question: "What would you like to hear about?",
      topicLabel: "Topic",
      topicPlaceholder: "Select a topic",
      topicOptions: [
        { value: "love", label: "Love" },
        { value: "work", label: "Work" },
        { value: "decision", label: "A decision" },
        { value: "self", label: "Yourself" },
      ],
      revealButton: "Draw a card",
      fields: {
        cardMeaning: "Card meaning",
        reflection: "Reflection",
        guidance: "Gentle guidance",
      },
    },
  },
};
